import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSiteCore } from "../../../../hooks/useSiteCore";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetProjectPrimaryDetailsById,
  useProjectActivationMutation,
  useSaveProjectFeeDetailsMutation,
  useSaveProjectPrimaryDetailsMutation,
  useUpdateProjectFeeDetailsMutation,
  useUpdateProjectPrimaryDetailsMutation,
} from "../../../../api/services/project/projectApis";
import {
  ITabs,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../components";
import {
  BILLING_DETAILS_SITE_CORE_VALUE,
  MAX_PERCENTAGE,
  PAGE_URLS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  getParsedErrorMessage,
  getValueOrBlank,
  isNotEmpty,
  mapProjectFacilitatorToTableData,
  mapSaleCreditToTableData,
  scrollToTopContainer,
} from "../../../../helpers/utils";
import { useToast } from "../../../../hooks/useToast";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  childProgramNavigationSelector,
  keyDetailsSelector,
} from "../../../../redux/slices/project/projectSelector";
import { getProgramFeePayload } from "../../../createProgram/helper";
import { getProjectPrimaryDetailsPayload } from "../../helper";
import {
  createProjectProgramFeeTabDefaultValue,
  createProjectTabDefaultValue,
  ProjectFeeTabFormKey,
  ProjectFeeTabFormType,
  ProjectTabFormKey,
  ProjectTabFormType,
} from "../../modelsAndSchemas/Models";

import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { clearStartChildProgramNavigation } from "../../../../redux/slices/project/projectSlice";
import { mapProjectStatusHistoryToTableData } from "../../../viewProject/components/helper";
import { ProjectDetails } from "../../../viewProject/modelsAndSchemas/models";
import {
  CreateProjectProjectTabSchema,
  ProjectFeeTabSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import {
  keyProjectDetailsSiteCoreKeys,
  projectFeeDetailsCoreKeys,
  salesCreditSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { CreateProjectFooter } from "../createProjectFooter/CreateProjectFooter";
import { CreateProgramTabsWrapper } from "./CreateProjectTabs.style";
import { ChildProgramsMain } from "./tabs/childPrograms/ChildProgramsMain";
import { ProjectFeesAndExpenseMain } from "./tabs/feesAndExpense/ProjectFeesAndExpenseMain";
import { ProjectTab } from "./tabs/project/ProjectTab";
import { ProjectFeeTab } from "./tabs/projectFee/ProjectFeeTab";

interface CreateProjectTabsProps {
  pageId: string;
  handleNavigateBack: () => void;
  projectId: string;
  isEditProject?: boolean;
  tabValue?: number;
  pageUrl: string;
  isNavigatedFromProgram?: boolean;
  handleDirtyTabCallBack?: (isDirty: boolean) => void;
}

export const PROJECT_TAB_INDEX = {
  PROJECT: 0,
  CHILD_PROGRAM: 1,
  PROJECT_FEE: 2,
  FEES_AND_EXPENSES: 3,
};

export const CreateProjectTabs: FC<CreateProjectTabsProps> = ({
  pageId,
  handleNavigateBack,
  projectId,
  isEditProject = false,
  tabValue = PROJECT_TAB_INDEX.PROJECT,
  pageUrl,
  isNavigatedFromProgram,
  handleDirtyTabCallBack,
}) => {
  const dispatch = useDispatch();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const childProgramNavigation = useSelector(childProgramNavigationSelector);
  const location = useLocation();

  const {
    data: projectPrimaryDetails,
    isSuccess: isProjectPrimaryDetailsSuccess,
    refetchWithParams: getProjectPrimaryDetails,
    refetch: refetchProjectPrimaryDetails,
  } = useGetProjectPrimaryDetailsById();

  useEffect(() => {
    if (childProgramNavigation) {
      if (isEditProject) {
        setSelectedTab(PROJECT_TAB_INDEX.CHILD_PROGRAM);
        dispatch(clearStartChildProgramNavigation());
      }
    }
  }, [childProgramNavigation]);

  useEffect(() => {
    if (
      (isEditProject || childProgramNavigation) &&
      isNotEmpty(projectKeyDetails)
    ) {
      getProjectPrimaryDetails(projectKeyDetails.id);
    }
  }, [isEditProject, projectKeyDetails, childProgramNavigation]);

  useEffect(() => {
    if (
      isNotEmpty(projectPrimaryDetails) &&
      (isEditProject || childProgramNavigation) &&
      !siteCoreLoading
    ) {
      setValueOnForm(projectPrimaryDetails);
    }
  }, [projectPrimaryDetails, isEditProject, siteCoreLoading]);

  useEffect(() => {
    if (isProjectPrimaryDetailsSuccess && childProgramNavigation) {
      setSelectedTab(PROJECT_TAB_INDEX.CHILD_PROGRAM);
      dispatch(clearStartChildProgramNavigation());
    }
  }, [isProjectPrimaryDetailsSuccess]);

  const [selectedTab, setSelectedTab] = useState(tabValue);
  const nextTabIndexRef = useRef(tabValue);
  const [showScheduleTotalAmountWarning, setShowScheduleTotalAmountWarning] =
    useState<{
      isOpen: boolean;
      isPercentage: boolean;
    }>({
      isOpen: false,
      isPercentage: false,
    });
  const [refetchProgramFee, setRefetchProgramFee] = useState(false);
  const [showUpdateConfirmationModal, setShowUpdateConfirmationModal] =
    useState(false);
  const [showSalesCreditMinimumModal, setShowSalesCreditMinimumModal] =
    useState(false);
  const [showPOCurrencyNotMatchModal, setShowPOCurrencyNotMatchModal] =
    useState(false);
  const navigate = useNavigate();
  const [showSalesCreditPercentageModal, setShowSalesCreditPercentageModal] =
    useState(false);
  const [isSaveProjectAPISucceedOnce, setIsSaveProjectAPISucceedOnce] =
    useState(false);

  const {
    fetchLabel,
    commonValidationMessages,
    commonLabels,
    fetchCommonLabel,
    commonBillingConfigurationDetails,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const { displayToast } = useToast();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    mutateAsync: saveProjectPrimaryDetailsApi,
    isPending: isSaveProjectPrimaryDetailsPending,
    isError: isSaveProjectPrimaryDetailsError,
    isSuccess: isSaveProjectPrimaryDetailsSuccess,
    data: savedProjectPrimaryDetailsId,
    error: saveProjectPrimaryDetailsError,
  } = useSaveProjectPrimaryDetailsMutation();

  const {
    mutateAsync: updateProjectPrimaryDetailsApi,
    isPending: isUpdateProjectPrimaryDetailsPending,
    isError: isUpdateProjectPrimaryDetailsError,
    isSuccess: isUpdateProjectPrimaryDetailsSuccess,
    error: updateProjectPrimaryDetailsError,
  } = useUpdateProjectPrimaryDetailsMutation();

  const {
    mutateAsync: saveProjectFeeDetailsApi,
    isPending: isSaveProgramFeeDetailsPending,
    isError: isSaveProgramFeeDetailsError,
    isSuccess: isSaveProgramFeeDetailsSuccess,
    error: saveProgramFeeDetailsError,
  } = useSaveProjectFeeDetailsMutation();

  const {
    mutateAsync: updateProjectFeeDetailsApi,
    isPending: isUpdateProgramFeeDetailsPending,
    isError: isUpdateProgramFeeDetailsError,
    isSuccess: isUpdateProgramFeeDetailsSuccess,
    error: updateProgramFeeDetailsError,
  } = useUpdateProjectFeeDetailsMutation();

  const isEditProjectPrimaryDetails = useMemo(() => {
    return (
      isEditProject || !!savedProjectPrimaryDetailsId || !!projectPrimaryDetails
    );
  }, [isEditProject, savedProjectPrimaryDetailsId, projectPrimaryDetails]);

  const {
    mutateAsync: projectActivation,
    isPending: isProjectActivationPending,
    isError: isProjectActivationError,
    isSuccess: isProjectActivationSuccess,
    error: projectActivationError,
  } = useProjectActivationMutation();

  const getTabLabel = (key: string) => {
    return fetchLabel(keyProjectDetailsSiteCoreKeys.keyProjectDetails, key);
  };
  const tabIdProject = `${pageId}-tab1`;
  const tabIdChildProgram = `${pageId}-tab2`;
  const tabIdProjectFee = `${pageId}-tab3`;
  const tabIdFeesAndExpenses = `${pageId}-tab4`;

  const tabs: ITabs[] = [
    {
      id: tabIdProject,
      label: getTabLabel(keyProjectDetailsSiteCoreKeys.projectTab),
    },
    {
      id: tabIdChildProgram,
      label: getTabLabel(keyProjectDetailsSiteCoreKeys.childProgramsTab),
    },
    {
      id: tabIdProjectFee,
      label: getTabLabel(keyProjectDetailsSiteCoreKeys.projectFeeTab),
    },
    {
      id: tabIdFeesAndExpenses,
      label: getTabLabel(keyProjectDetailsSiteCoreKeys.feeAndExpenseTab),
    },
  ];

  const getApiErrorByStatus = () => {
    if (isUpdateProgramFeeDetailsError) {
      return updateProgramFeeDetailsError;
    } else if (isSaveProgramFeeDetailsError) {
      return saveProgramFeeDetailsError;
    } else if (isSaveProjectPrimaryDetailsError) {
      return saveProjectPrimaryDetailsError;
    } else if (isUpdateProjectPrimaryDetailsError) {
      return updateProjectPrimaryDetailsError;
    } else if (isProjectActivationError) {
      return projectActivationError;
    }
  };

  const isError =
    isSaveProgramFeeDetailsError ||
    isUpdateProgramFeeDetailsError ||
    isSaveProjectPrimaryDetailsError ||
    isUpdateProjectPrimaryDetailsError ||
    isProjectActivationError;

  const isLoading =
    isSaveProgramFeeDetailsPending ||
    isUpdateProgramFeeDetailsPending ||
    isSaveProjectPrimaryDetailsPending ||
    isUpdateProjectPrimaryDetailsPending ||
    isProjectActivationPending;
  useEffect(() => {
    if (isSaveProgramFeeDetailsError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
    const error = getApiErrorByStatus();
    if (error) {
      const parsedErrorMessage = getParsedErrorMessage(error);
      displayToast(parsedErrorMessage, "error");
    }
  }, [isError]);

  useEffect(() => {
    if (isLoading) {
      dispatch(
        toggleLoader({
          isVisible: true,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isLoading]);

  useEffect(() => {
    if (
      isSaveProjectPrimaryDetailsSuccess ||
      isUpdateProjectPrimaryDetailsSuccess
    ) {
      if (!isSaveProjectAPISucceedOnce && isSaveProjectPrimaryDetailsSuccess) {
        setIsSaveProjectAPISucceedOnce(true);
      }
      setSelectedTab(
        isEditProject
          ? nextTabIndexRef.current
          : PROJECT_TAB_INDEX.CHILD_PROGRAM,
      );
      scrollToTopContainer(500);
    }
  }, [
    isSaveProjectPrimaryDetailsSuccess,
    isUpdateProjectPrimaryDetailsSuccess,
  ]);

  useEffect(() => {
    if (selectedTab === PROJECT_TAB_INDEX.PROJECT_FEE) {
      setRefetchProgramFee(!refetchProgramFee);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (isSaveProgramFeeDetailsSuccess) {
      setRefetchProgramFee(!refetchProgramFee);
    }
    if (
      (isSaveProgramFeeDetailsSuccess || isUpdateProgramFeeDetailsSuccess) &&
      projectId
    ) {
      setSelectedTab(
        isEditProject
          ? nextTabIndexRef.current
          : PROJECT_TAB_INDEX.FEES_AND_EXPENSES,
      );
      scrollToTopContainer(500);
    }
  }, [isSaveProgramFeeDetailsSuccess, isUpdateProgramFeeDetailsSuccess]);

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    if (selectedTab === PROJECT_TAB_INDEX.PROJECT_FEE) {
      setRefetchProgramFee(!refetchProgramFee);
    }

    // Prevent a keyboard/click-initiated tab switch from overwriting the
    // destination of a save that is still in flight.
    if (isLoading) {
      return;
    }
    if (
      value < selectedTab ||
      selectedTab === PROJECT_TAB_INDEX.FEES_AND_EXPENSES
    ) {
      nextTabIndexRef.current = value;
      setSelectedTab(value);
    } else {
      handleContinueButtonClick(value);
    }
  };

  const methodsProject = useForm<ProjectTabFormType>({
    mode: "all",
    defaultValues: createProjectTabDefaultValue,
    resolver: yupResolver(
      CreateProjectProjectTabSchema(commonValidationMessages),
    ),
  });

  const {
    reset: projectReset,
    handleSubmit: projectTabSubmit,
    getValues: getPrimaryDetailsFormValues,
    setValue: setValueProjectTab,
    formState: { isDirty: isProjectTabDirty },
  } = methodsProject;

  useEffect(() => {
    if (isNotEmpty(projectKeyDetails) && !isEditProject) {
      projectReset(
        {
          ...getPrimaryDetailsFormValues(),
          billingOffice: projectKeyDetails?.billingOffice?.name,
          salesOfficeId: projectKeyDetails?.salesOffice?.name,
        },
        { keepDirtyValues: true },
      );
    } else {
      setValueProjectTab(
        ProjectTabFormKey.billingOffice,
        getValueOrBlank(projectKeyDetails?.billingOffice?.name),
      );
      setValueProjectTab(
        ProjectTabFormKey.salesOfficeId,
        getValueOrBlank(projectKeyDetails?.salesOffice?.name),
      );
    }
  }, [projectKeyDetails]);

  const setValueOnForm = (primaryDetails: ProjectDetails) => {
    setValueProjectTab(
      ProjectTabFormKey.salesCreditDetails,
      mapSaleCreditToTableData(primaryDetails?.salesCredit, false),
    );

    const facilitatorList = primaryDetails?.projectFacilitator
      ? mapProjectFacilitatorToTableData(
          primaryDetails?.projectFacilitator?.projectFacilitatorTypeAssignments,
          fetchValueByKeyOfDropdown,
          getValueOrBlank(userCountryDateFormat),
        )
      : [];
    setValueProjectTab(ProjectTabFormKey.projectFacilitator, facilitatorList);
    const officeDetails = primaryDetails?.officeAndContactDetails;
    setValueProjectTab(
      ProjectTabFormKey.billingContact,
      getValueOrBlank(officeDetails?.billingContact?.id),
    );
    setValueProjectTab(
      ProjectTabFormKey.hrContact,
      getValueOrBlank(officeDetails?.hrContact?.id),
    );
    setValueProjectTab(
      ProjectTabFormKey.projectManager,
      officeDetails?.projectManager?.map((pm) => pm.id) || [],
    );
    setValueProjectTab(
      ProjectTabFormKey.projectCoordinator,
      officeDetails?.projectCoordinator?.map((pm) => pm.id) || [],
    );
    setValueProjectTab(
      ProjectTabFormKey.clientRelationShipOwner,
      getValueOrBlank(officeDetails?.localClientRelationshipOwner?.id),
    );
  };

  const checkSalesCreditValidation = () => {
    const { salesCreditDetails } = getPrimaryDetailsFormValues();
    let isSalesCreditValid = true;
    if ((salesCreditDetails?.length ?? 0) <= 0) {
      setShowSalesCreditMinimumModal(true);
      isSalesCreditValid = false;
    } else {
      const totalPercentage = ((salesCreditDetails as any) ?? []).reduce(
        (sum: number, item: { percentage?: number }) =>
          sum + (Number(item.percentage) || 0),
        0,
      );
      if (totalPercentage !== MAX_PERCENTAGE) {
        setShowSalesCreditPercentageModal(true);
        isSalesCreditValid = false;
      }
    }
    return isSalesCreditValid;
  };

  const callProjectDetailsApi = () => {
    if (!checkSalesCreditValidation()) return;

    if (isProjectTabDirty) {
      const formValues = getPrimaryDetailsFormValues();
      const payload = getProjectPrimaryDetailsPayload({
        formValues,
        projectId,
        userCountryDateFormat: userCountryDateFormat as string,
      });
      if (isEditProjectPrimaryDetails) {
        updateProjectPrimaryDetailsApi(payload);
      } else {
        saveProjectPrimaryDetailsApi(payload);
      }
    } else {
      setSelectedTab(
        isEditProject
          ? nextTabIndexRef.current
          : PROJECT_TAB_INDEX.CHILD_PROGRAM,
      );
      scrollToTopContainer(500);
    }
  };

  useEffect(() => {
    if (isProjectActivationSuccess)
      navigate(`${PAGE_URLS.VIEW_PROJECT}/${projectId}`);
  }, [isProjectActivationSuccess]);

  const earlyPayPercentageErrorMessage = fetchLabel(
    projectFeeDetailsCoreKeys.projectFeeDetails,
    projectFeeDetailsCoreKeys.earlyPayPercentageToolTipMessage,
  );

  const minimumTransferFeeShouldBeLessThanProgramFee = fetchLabel(
    projectFeeDetailsCoreKeys.projectFeeDetails,
    projectFeeDetailsCoreKeys.minimumTransferFeeShouldBeLessThanProjectFee,
  );

  const programFeeShouldBeGreaterThanMinimumTransferFee = fetchLabel(
    projectFeeDetailsCoreKeys.projectFeeDetails,
    projectFeeDetailsCoreKeys.projectFeeShouldBeGreaterThanMinimumTransferFee,
  );

  const methodsProjectFee = useForm<ProjectFeeTabFormType>({
    mode: "all",
    defaultValues: createProjectProgramFeeTabDefaultValue,
    resolver: yupResolver(
      ProjectFeeTabSchema(
        commonValidationMessages,
        earlyPayPercentageErrorMessage,
        minimumTransferFeeShouldBeLessThanProgramFee,
        programFeeShouldBeGreaterThanMinimumTransferFee,
      ),
    ),
  });

  const {
    handleSubmit: projectFeeTabSubmit,
    getValues: projectFeeGetValues,
    formState: { isDirty: isProjectFeeTabDirty },
  } = methodsProjectFee;

  useEffect(() => {
    if (isNavigatedFromProgram) {
      handleDirtyTabCallBack?.(isProjectTabDirty || isProjectFeeTabDirty);
    }
  }, [isNavigatedFromProgram, isProjectTabDirty, isProjectFeeTabDirty]);

  const handleContinueButtonClick = (nextIndex: number) => {
    if (isLoading) {
      return;
    }
    nextTabIndexRef.current = nextIndex;
    if (selectedTab === PROJECT_TAB_INDEX.PROJECT) {
      projectTabSubmit(callProjectDetailsApi)();
    } else if (selectedTab === PROJECT_TAB_INDEX.CHILD_PROGRAM) {
      setSelectedTab(
        isEditProject ? nextTabIndexRef.current : PROJECT_TAB_INDEX.PROJECT_FEE,
      );
    } else if (selectedTab === PROJECT_TAB_INDEX.PROJECT_FEE) {
      if (isEditProject && isProjectFeeTabDirty) {
        setShowUpdateConfirmationModal(true);
      } else {
        projectFeeTabSubmit(validateAndCallProjectFeeApi)();
      }
    } else if (selectedTab === PROJECT_TAB_INDEX.FEES_AND_EXPENSES) {
      if (isEditProject) {
        navigate(`${PAGE_URLS.VIEW_PROJECT}/${projectId}`, {
          replace: true,
          state: {
            ...location.state,
          },
        });
      } else {
        projectActivation(projectId);
      }
    }
  };

  const watchProgramFeeBillingCurrency = useWatch({
    control: methodsProjectFee.control,
    name: ProjectFeeTabFormKey.currencyId,
    defaultValue: "",
  });

  const validateAndCallProjectFeeApi = () => {
    const { billingSchedules, billingTypeId, feePaymentType, fee } =
      projectFeeGetValues();
    if (
      [
        BILLING_DETAILS_SITE_CORE_VALUE.billingTypeMilestone,
        BILLING_DETAILS_SITE_CORE_VALUE.billingTypeSchedule,
      ].includes(billingTypeId)
    ) {
      let totalAmountOrPercentage = 0;
      const isPercentage =
        feePaymentType === BILLING_DETAILS_SITE_CORE_VALUE.valuePercentage;
      billingSchedules?.forEach((item) => {
        totalAmountOrPercentage += isPercentage
          ? Number(item.percentage)
          : Number(item?.amount);
      });
      if (
        (isPercentage && totalAmountOrPercentage === MAX_PERCENTAGE) ||
        (!isPercentage && Number(fee) === totalAmountOrPercentage)
      ) {
        callProjectFeeApi();
      } else {
        setShowScheduleTotalAmountWarning({
          isOpen: true,
          isPercentage,
        });
      }
    } else {
      callProjectFeeApi();
    }
  };

  const handlePOCurrencyNotMatchWarningModalClose = () => {
    setShowPOCurrencyNotMatchModal(false);
  };

  const handleScheduleAmountWarningModalClose = () => {
    setShowScheduleTotalAmountWarning((prev) => ({ ...prev, isOpen: false }));
  };

  const handleSalesCreditPercentageModalClose = () => {
    setShowSalesCreditPercentageModal(false);
  };

  const handleSalesCreditMinimumModalClose = () => {
    setShowSalesCreditMinimumModal(false);
  };

  const callProjectFeeApi = () => {
    const formValues = projectFeeGetValues();
    const [requestPayload, isUpdate] = getProgramFeePayload(formValues);
    if (isUpdate) {
      if (isProjectFeeTabDirty) {
        updateProjectFeeDetailsApi({
          params: requestPayload,
          projectId: projectId,
        });
      } else {
        setSelectedTab(
          isEditProject
            ? nextTabIndexRef.current
            : PROJECT_TAB_INDEX.FEES_AND_EXPENSES,
        );
      }
    } else {
      saveProjectFeeDetailsApi({
        params: requestPayload,
        projectId: projectId,
      });
    }
  };

  const handleBackButtonClick = () => {
    const selectedTabIndex = selectedTab === 0 ? 0 : selectedTab - 1;
    setSelectedTab(selectedTabIndex);
    scrollToTopContainer(500);
  };

  const getTabButtonLabel = (): string => {
    if (isEditProject) return commonLabels.update;
    if (selectedTab === PROJECT_TAB_INDEX.FEES_AND_EXPENSES)
      return commonLabels.submit;
    return commonLabels.saveAndNextButtonText;
  };

  const projectStatusRows = useMemo(() => {
    if (isNotEmpty(projectPrimaryDetails)) {
      return mapProjectStatusHistoryToTableData(
        projectPrimaryDetails?.projectStatus?.projectStatusHistoryResponse ??
          [],
      );
    } else return [];
  }, [projectPrimaryDetails]);

  // const onHandleDeleteTransferApi = () => {
  //   deleteTransferActivity(projectId);
  // };

  const handleUpdateConfirmationModalClose = () => {
    setShowUpdateConfirmationModal(false);
  };

  const handleUpdateConfirmationModalPrimaryButtonClick = () => {
    projectFeeTabSubmit(validateAndCallProjectFeeApi)();
    setShowUpdateConfirmationModal(false);
  };

  return (
    <CreateProgramTabsWrapper>
      <MuiTabs
        tabs={tabs}
        value={selectedTab}
        onChange={onTabChange}
        tabsTestIds={[
          tabIdProject,
          tabIdChildProgram,
          tabIdProjectFee,
          tabIdFeesAndExpenses,
        ]}
        className="program-candidate-tab"
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={selectedTab}
          index={PROJECT_TAB_INDEX.PROJECT}
          isPanelWithMount={false}
          ariaLabelledById={tabIdProject}
        >
          <FormProvider {...methodsProject}>
            <ProjectTab
              pageId={pageId}
              isEditProject={isEditProject}
              projectId={projectId}
              onRefetchPrimaryDetails={refetchProjectPrimaryDetails}
              projectStatusList={projectStatusRows}
              isSaveProjectApiCalledOnce={isSaveProjectAPISucceedOnce}
            />
          </FormProvider>
        </MuiTabPanel>

        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={selectedTab}
          index={PROJECT_TAB_INDEX.CHILD_PROGRAM}
          isPanelWithMount
          ariaLabelledById={tabIdChildProgram}
        >
          <ChildProgramsMain
            projectId={projectId}
            isEditProject={isEditProject}
            pageUrl={pageUrl}
            isNavigatedFromProgram={isNavigatedFromProgram}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel3`}
          value={selectedTab}
          index={PROJECT_TAB_INDEX.PROJECT_FEE}
          isPanelWithMount
          ariaLabelledById={tabIdProjectFee}
        >
          <FormProvider {...methodsProjectFee}>
            <ProjectFeeTab
              pageId={pageId}
              projectId={projectId}
              isEditProject={isEditProject}
              refetchProjectFee={refetchProgramFee}
            />
          </FormProvider>
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel4`}
          value={selectedTab}
          index={PROJECT_TAB_INDEX.FEES_AND_EXPENSES}
          isPanelWithMount
          ariaLabelledById={tabIdFeesAndExpenses}
        >
          <ProjectFeesAndExpenseMain
            billingCurrency={watchProgramFeeBillingCurrency}
            isRefetch={selectedTab === PROJECT_TAB_INDEX.FEES_AND_EXPENSES}
            isEditProject={isEditProject}
          />
        </MuiTabPanel>
      </MuiTabs>

      <CreateProjectFooter
        pageId={pageId}
        onConfirmClick={() => handleContinueButtonClick(selectedTab + 1)}
        handleNavigateBack={handleNavigateBack}
        confirmButtonText={getTabButtonLabel()}
        isShowBackButton={selectedTab > 0 && !isEditProject}
        handleBackButtonClick={handleBackButtonClick}
        isFiledUpdated={isProjectTabDirty}
        cancelMessage={fetchCommonLabel(
          isEditProject
            ? commonSiteCoreKeys.closeButtonActionText
            : commonSiteCoreKeys.cancelConfirmationText,
        )}
        cancelButtonText={
          isEditProject ? commonLabels.close : commonLabels.cancel
        }
        confirmationTitle={
          isEditProject ? commonLabels.warning : commonLabels.cancelChanges
        }
      />

      {showScheduleTotalAmountWarning.isOpen && (
        <WarningConfirmationModal
          show={showScheduleTotalAmountWarning.isOpen}
          modalTitle={commonLabels.warning}
          onClose={handleScheduleAmountWarningModalClose}
          onPrimaryButtonClick={handleScheduleAmountWarningModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={
            showScheduleTotalAmountWarning.isPercentage
              ? commonValidationMessages.totalPercentageMustEqual100
              : commonBillingConfigurationDetails.totalAmountMustEqualProjectFeeErrorMessage
          }
        />
      )}

      {showUpdateConfirmationModal && (
        <WarningConfirmationModal
          labelContent={fetchLabel(
            projectFeeDetailsCoreKeys.projectFeeDetails,
            projectFeeDetailsCoreKeys.areYouSureYouWantToChangeTheProjectFee,
          )}
          modalTitle={commonLabels.confirmation}
          show={showUpdateConfirmationModal}
          onClose={handleUpdateConfirmationModalClose}
          onPrimaryButtonClick={handleUpdateConfirmationModalPrimaryButtonClick}
          primaryButtonTitle={commonLabels.yes}
          secondaryButtonTitle={commonLabels.no}
        />
      )}

      {showPOCurrencyNotMatchModal && (
        <WarningConfirmationModal
          show={showPOCurrencyNotMatchModal}
          modalTitle={commonLabels.warning}
          onClose={handlePOCurrencyNotMatchWarningModalClose}
          onPrimaryButtonClick={handlePOCurrencyNotMatchWarningModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            projectFeeDetailsCoreKeys.projectFeeDetails,
            projectFeeDetailsCoreKeys.projectFeeCurrencyHasToBeSameAsPOCurrency,
          )}
        />
      )}
      {showSalesCreditMinimumModal && (
        <WarningConfirmationModal
          show={showSalesCreditMinimumModal}
          modalTitle={commonLabels.warning}
          onClose={handleSalesCreditMinimumModalClose}
          onPrimaryButtonClick={handleSalesCreditMinimumModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            salesCreditSiteCoreKeys.salesCredit,
            salesCreditSiteCoreKeys.minimumOneClientRelationshipOwnerRequired,
          )}
        />
      )}
      {showSalesCreditPercentageModal && (
        <WarningConfirmationModal
          show={showSalesCreditPercentageModal}
          modalTitle={commonLabels.warning}
          onClose={handleSalesCreditPercentageModalClose}
          onPrimaryButtonClick={handleSalesCreditPercentageModalClose}
          showSecondaryButton={false}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            salesCreditSiteCoreKeys.salesCredit,
            salesCreditSiteCoreKeys.totalPercentageMustBe100,
          )}
        />
      )}
    </CreateProgramTabsWrapper>
  );
};
